import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, FileDown, FileSpreadsheet, FileText, Loader2, Receipt, Wallet } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { FilingForm } from "./FilingForm";
import { SummaryTable } from "./SummaryTable";
import { DocsTable } from "./DocsTable";
import { downloadBlob, fmtDate, money, signedMoney } from "./_shared";

export function PeriodDetail({ period, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sales");
  const [showFiling, setShowFiling] = useState(false);
  const [dl, setDl] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/cabinet-taxes/periods/${period.key}`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  }, [period.key]);
  useEffect(() => { setData(null); load(); }, [load, period.filing]);
  const changed = () => { load(); onChanged(); };

  const download = async (fmt) => {
    setDl(fmt);
    try {
      const r = await api.get(`/admin/cabinet-taxes/periods/${period.key}/report.${fmt}`, { responseType: "blob" });
      if (fmt === "pdf") {
        const url = URL.createObjectURL(r.data);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        downloadBlob(r.data, `taxes-tps-tvq-${period.key}.${fmt}`);
      }
    } catch { toast.error("Export impossible"); }
    finally { setDl(""); }
  };

  const totals = data?.totals || period;

  return (
    <div className="border-t bg-slate-50/70 p-4 space-y-4" data-testid={`period-detail-${period.key}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
        <SummaryTable period={period} totals={totals} onSaved={changed} />

        <div className="space-y-3">
          <div className="bg-white border rounded-xl p-3 space-y-2">
            <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">Rapport de la période</div>
            <div className="grid grid-cols-3 gap-2">
              {[["pdf", "PDF", FileDown], ["xlsx", "Excel", FileSpreadsheet], ["csv", "CSV", FileText]].map(([f, l, I]) => (
                <button key={f} onClick={() => download(f)} disabled={!!dl}
                  className="border rounded-lg px-2 py-2 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`report-${f}-${period.key}`}>
                  {dl === f ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <I className="w-3.5 h-3.5" />} {l}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-3 space-y-2">
            <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">Déclaration</div>
            {period.filing && period.filing.status !== "to_file" ? (
              <div className="text-xs text-slate-600 space-y-0.5" data-testid={`filing-summary-${period.key}`}>
                <div>Produite le <strong>{fmtDate(period.filing.filed_at)}</strong></div>
                {period.filing.status === "paid" && <div>Payée le <strong>{fmtDate(period.filing.paid_at)}</strong> — <strong>{money(period.filing.paid_amount)}</strong></div>}
                {period.filing.confirmation_number && <div>Confirmation : <span className="font-mono">{period.filing.confirmation_number}</span></div>}
                {period.filing.note && <div className="italic">{period.filing.note}</div>}
                {period.drift && (
                  <div className="flex items-start gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1" data-testid={`filing-drift-${period.key}`}>
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Les montants ont changé depuis la production (déclaré : {signedMoney(period.filing.snapshot?.net_total)}). Une déclaration modifiée peut être nécessaire.</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500">Échéance : <strong>{fmtDate(period.due_date)}</strong>. {period.in_progress ? "Période en cours — les chiffres bougent encore." : "Période terminée : produis ta déclaration puis marque-la ici."}</p>
            )}
            <button onClick={() => setShowFiling(!showFiling)}
              className="w-full bg-indigo-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-indigo-700"
              data-testid={`filing-open-${period.key}`}>
              {period.filing && period.filing.status !== "to_file" ? "Modifier le statut" : "Marquer produite / payée"}
            </button>
          </div>
        </div>
      </div>

      {showFiling && <FilingForm period={period} onClose={() => setShowFiling(false)} onSaved={() => { setShowFiling(false); onChanged(); }} />}

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="flex border-b text-sm">
          {[["sales", "Ventes — taxes perçues", Receipt, data?.sales?.length], ["purchases", "Achats — CTI / RTI", Wallet, data?.purchases?.length]].map(([id, l, I, n]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 border-b-2 -mb-px ${tab === id ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`detail-tab-${id}-${period.key}`}>
              <I className="w-3.5 h-3.5" /> {l} {n != null && <span className="text-[10px] bg-slate-100 rounded-full px-1.5">{n}</span>}
            </button>
          ))}
          <span className="ml-auto self-center pr-3 text-[11px] text-slate-400 hidden md:inline">Crayon = corriger la TPS / TVQ d'une pièce à la source</span>
        </div>
        {!data ? (
          <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <DocsTable side={tab} rows={data[tab]} onSaved={changed} />
        )}
      </div>
    </div>
  );
}
